import React, { createContext, useContext, useState } from 'react';
import { API_BASE_URL } from '../api/apiService';

const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if token exists in localStorage on initial load
    return !!localStorage.getItem('access_token');
  });
  const [loading, setLoading] = useState(false);

  // Login function
  const login = async (apiKey) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ x_api_key: apiKey }),
      });

      if (!response.ok) {
        // Try to get error details from response
        let errorDetail = 'Login failed';
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || errorDetail;
        } catch (e) {
          // If response is not JSON, use status text
          errorDetail = response.statusText || errorDetail;
        }
        throw new Error(errorDetail);
      }

      const data = await response.json();

      // Store the token in localStorage
      localStorage.setItem('access_token', data.access_token);

      // Update authentication state - this will trigger App.js to re-render
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
  };

  // Function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  // Verify authentication status
  const verifyAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsAuthenticated(false);
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setIsAuthenticated(true);
        return true;
      } else {
        // Token is invalid/expired, remove it
        localStorage.removeItem('access_token');
        setIsAuthenticated(false);
        return false;
      }
    } catch (error) {
      console.error('Auth verification error:', error);
      localStorage.removeItem('access_token');
      setIsAuthenticated(false);
      return false;
    }
  };

  const value = {
    isAuthenticated,
    loading,
    login,
    logout,
    getAuthHeaders,
    verifyAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};